import { motion } from "framer-motion";
import { PhoneCall, Cpu, Rocket, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import AIComparisonMockup from "./AIComparisonMockup";

const HowItWorks = () => {
  const steps = [
    {
      icon: PhoneCall,
      step: "01",
      title: "Discovery Call",
      description: "We learn about your business, target customers, and goals. You tell us what you need, we handle the rest.",
      details: ["15-minute call", "No obligation", "Custom strategy"],
    },
    {
      icon: Cpu,
      step: "02",
      title: "AI-Powered Campaign Creation",
      description: "Our automated systems generate ad creatives, targeting, and copy optimized for AI platforms.",
      details: ["AI-generated ads", "Smart targeting", "A/B testing"],
    },
    {
      icon: Rocket,
      step: "03",
      title: "Launch & Optimize",
      description: "We launch your campaigns and continuously monitor performance. Watch the leads flow in.",
      details: ["24/7 monitoring", "Weekly reports", "Ongoing optimization"],
    },
  ];

  return (
    <section id="how-it-works" className="py-12 md:py-16 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />
      
      <div className="container mx-auto px-4 max-w-[1280px] relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center space-y-4 mb-10"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
            How <span className="text-gradient">AI Ads Flow</span> Works
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            We've automated the entire process. You focus on running your business, 
            we focus on bringing you leads.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Steps */}
          <div className="space-y-4">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="relative"
              >
                <div className="relative glass rounded-2xl p-5 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center shrink-0">
                      <step.icon className="h-6 w-6 text-accent-foreground" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-2xl font-bold text-accent">
                          {step.step}
                        </span>
                        <h3 className="text-base font-semibold">{step.title}</h3>
                      </div>
                      <p className="text-muted-foreground text-sm mb-3 leading-relaxed">{step.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {step.details.map((detail, i) => (
                          <span key={i} className="flex items-center gap-1.5 text-xs bg-secondary/50 border border-border/50 px-3 py-1.5 rounded-full">
                            <CheckCircle2 className="h-3 w-3 text-accent" />
                            {detail}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* AI Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <AIComparisonMockup />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-10"
        >
          <p className="text-lg text-muted-foreground mb-4">
            <span className="text-foreground font-semibold">Zero effort on your part.</span>{" "}
            We handle everything from strategy to execution.
          </p>
          <Button 
            className="bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 transition-all"
            asChild
          >
            <a href="#contact">
              GET STARTED TODAY
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
